import { getToken, onMessage } from 'firebase/messaging'
import * as Sentry from '@sentry/react'
import { getMessagingInstance } from './firebase'
import { registerPushToken, unregisterPushToken } from '../api/push'

/** True if this browser/PWA context can support web push at all (SW + Notification API + FCM). */
export async function isPushSupported() {
  if (typeof Notification === 'undefined' || !('serviceWorker' in navigator)) return false
  const messaging = await getMessagingInstance()
  return Boolean(messaging)
}

/**
 * Requests notification permission and returns an FCM registration token,
 * or null if permission was denied / push is unsupported in this browser.
 */
export async function requestPushPermission() {
  if (typeof Notification === 'undefined' || !('serviceWorker' in navigator)) return null

  const messaging = await getMessagingInstance()
  if (!messaging) return null

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return null

  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY
  if (!vapidKey) {
    console.warn('VITE_FIREBASE_VAPID_KEY is not set — cannot register for push')
    return null
  }

  const serviceWorkerRegistration = await navigator.serviceWorker.ready
  const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration })
  if (token) {
    console.info(
      `[push][audit] token acquired token=${token.slice(0, 12)}… scope=${serviceWorkerRegistration.scope} at=${new Date().toISOString()}`
    )
  }
  return token
}

/**
 * Requests permission and registers the device for push. Must be called
 * from a real user gesture (e.g. a button onClick) — Chrome throws if
 * Notification.requestPermission() runs outside one. Silently no-op on
 * failure (denied permission, unsupported browser, etc).
 */
export async function initPushOnLogin(tenantId) {
  if (!tenantId) return
  console.info(`[push][audit] initPushOnLogin called tenant=${tenantId} at=${new Date().toISOString()}`)
  try {
    const token = await requestPushPermission()
    if (token) await registerPushToken(tenantId, token)
  } catch (err) {
    console.warn('Push init on login failed (non-fatal):', err)
    Sentry.captureException(err, { tags: { action: 'push_init_on_login' }, extra: { tenantId } })
  }
}

/**
 * Re-subscribes devices whose Notification permission is already 'granted'
 * (e.g. from a previous session) but that may not have a live registration
 * on the backend — the permission-prompt modal only ever fires once while
 * permission is 'default', so this is the only path that recovers devices
 * left behind by a failed getToken()/register call in an earlier session.
 * Safe to call on every app load: requestPermission() on an already-granted
 * permission resolves instantly with no prompt, and the backend upserts by
 * token so repeat registrations are idempotent.
 */
export async function ensurePushSubscription(tenantId) {
  if (!tenantId) return
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
  console.info(`[push][audit] ensurePushSubscription called tenant=${tenantId} at=${new Date().toISOString()}`)
  try {
    const token = await requestPushPermission()
    if (token) await registerPushToken(tenantId, token)
  } catch (err) {
    console.warn('Push auto re-subscribe failed (non-fatal):', err)
    Sentry.captureException(err, { tags: { action: 'push_auto_resubscribe' }, extra: { tenantId } })
  }
}

/**
 * Unregisters this device's push token on logout so the old session's tenant
 * stops receiving pushes on this browser. Best-effort, silently no-op on failure.
 */
export async function clearPushOnLogout(tenantId) {
  if (!tenantId) return
  console.info(`[push][audit] clearPushOnLogout called tenant=${tenantId} at=${new Date().toISOString()}`)
  try {
    const token = await requestPushPermission()
    if (token) await unregisterPushToken(tenantId, token)
  } catch (err) {
    console.warn('Push cleanup on logout failed (non-fatal):', err)
    Sentry.captureException(err, { tags: { action: 'push_clear_on_logout' }, extra: { tenantId } })
  }
}

/** Subscribes to push messages received while the tab is focused. Returns an unsubscribe function. */
export async function onForegroundPush(callback) {
  const messaging = await getMessagingInstance()
  if (!messaging) return () => {}
  return onMessage(messaging, callback)
}

/** Clears the app icon badge and any stacked background-push notifications. Call when the app gains focus. */
export async function clearAppBadge() {
  if (typeof navigator === 'undefined') return
  if ('clearAppBadge' in navigator) {
    try {
      await navigator.clearAppBadge()
    } catch {
      // Badging API not supported/permitted — ignore.
    }
  }
  if (!('serviceWorker' in navigator)) return
  const registration = await navigator.serviceWorker.getRegistration('/')
  if (!registration) return
  const notifications = await registration.getNotifications()
  notifications.forEach((n) => n.close())
}
